import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Text, Group, Button, Stack, Loader, Flex, Select, TextInput, Badge, Alert } from '@mantine/core';
import { Copy, Check, Plus } from '@phosphor-icons/react';
import api from '../utils/api';
import { showToast } from './Toast';

const STANDARD_EVENTS = [
  'CompleteRegistration', 'Lead', 'Purchase', 'StartTrial', 'Subscribe',
  'Contact', 'AddToCart', 'InitiateCheckout', 'PageView',
];

const snippetFor = (pixelId) => `<!-- Meta Pixel -->
<script>
!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');
</script>
<!-- End Meta Pixel -->`;

/**
 * Zarządzanie Pixelem Meta i konwersjami z poziomu droida: lista pixeli z
 * aktywnością zdarzeń (7 dni), tworzenie pixela, kod do wklejenia na stronę
 * oraz niestandardowe konwersje (zdarzenie standardowe i/lub fragment URL).
 */
export default function MetaPixelPanel() {
  const { t } = useTranslation();
  const [pixels, setPixels] = useState(null);
  const [conversions, setConversions] = useState([]);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showSnippet, setShowSnippet] = useState(null);
  const [showConvForm, setShowConvForm] = useState(false);
  const [convForm, setConvForm] = useState({ name: '', pixel_id: '', event: '', url_contains: '' });
  const [savingConv, setSavingConv] = useState(false);

  const load = () => {
    Promise.all([
      api.get('/ads/meta/pixels/'),
      api.get('/ads/meta/custom-conversions/').catch(() => ({ data: { conversions: [] } })),
    ])
      .then(([px, cv]) => {
        setPixels(px.data.pixels || []);
        setConversions(cv.data.conversions || []);
      })
      .catch(err => setError(err.response?.data?.error || t('conversions.metaPixel.toasts.fetchError')));
  };
  useEffect(load, []);

  const createPixel = async () => {
    setCreating(true);
    try {
      await api.post('/ads/meta/pixels/', { name: 'Pixel' });
      showToast(t('conversions.metaPixel.toasts.pixelCreated'), 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || t('conversions.metaPixel.toasts.pixelError'), 'error');
    } finally { setCreating(false); }
  };

  const copySnippet = (pixelId) => {
    navigator.clipboard.writeText(snippetFor(pixelId)).then(() => {
      setCopiedId(pixelId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const createConversion = async () => {
    if (!convForm.name || !convForm.pixel_id || (!convForm.event && !convForm.url_contains)) {
      showToast(t('conversions.metaPixel.toasts.convValidation'), 'error');
      return;
    }
    setSavingConv(true);
    try {
      await api.post('/ads/meta/custom-conversions/', convForm);
      showToast(t('conversions.metaPixel.toasts.convCreated'), 'success');
      setShowConvForm(false);
      setConvForm({ name: '', pixel_id: '', event: '', url_contains: '' });
      load();
    } catch (err) {
      showToast(err.response?.data?.error || t('conversions.metaPixel.toasts.convError'), 'error');
    } finally { setSavingConv(false); }
  };

  if (error) return <Paper withBorder p="md" radius="md"><Text size="sm" c="dimmed">{error}</Text></Paper>;
  if (pixels === null) return <Paper withBorder p="md" radius="md"><Flex justify="center" py="sm"><Loader size="sm" color="brand" /></Flex></Paper>;

  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={500}>{t('conversions.metaPixel.title')}</Text>
        {pixels.length === 0 && (
          <Button size="compact-xs" color="brand" leftSection={<Plus size={12} />} loading={creating} onClick={createPixel}>
            {t('conversions.metaPixel.createPixel')}
          </Button>
        )}
      </Group>
      <Text size="xs" c="dimmed" mb={6}>
        {t('conversions.metaPixel.description')}
      </Text>
      <Alert color="gray" variant="light" p="xs" mb="sm" styles={{ message: { fontSize: 12 } }}>
        {t('conversions.metaPixel.statsNote.intro1')}<b>{t('conversions.metaPixel.statsNote.wholeSite')}</b>{t('conversions.metaPixel.statsNote.intro2')}<b>{t('conversions.metaPixel.statsNote.last7d')}</b>{t('conversions.metaPixel.statsNote.intro3')}
        <br />
        <b>PageView</b> = {t('conversions.metaPixel.statsNote.pageViewDesc')} · <b>Contact</b> = {t('conversions.metaPixel.statsNote.contactDesc')} · <b>CompleteRegistration</b> = {t('conversions.metaPixel.statsNote.completeRegistrationDesc')}
        {' '}{t('conversions.metaPixel.statsNote.leadsHintPre')}<b>{t('conversions.metaPixel.statsNote.leadsTab')}</b>{t('conversions.metaPixel.statsNote.leadsHintPost')}
      </Alert>

      {pixels.length === 0 ? (
        <Text size="xs" c="dimmed">{t('conversions.metaPixel.emptyPixel')}</Text>
      ) : (
        <Stack gap="sm">
          {pixels.map(px => (
            <div key={px.id} className="border border-gray-100 rounded-md px-3 py-2">
              <Group justify="space-between">
                <div>
                  <Group gap="xs">
                    <Text size="sm" fw={500}>{px.name}</Text>
                    {px.last_fired_time
                      ? <Badge size="xs" variant="light" color="green">{t('conversions.metaPixel.badgeActive')}</Badge>
                      : <Badge size="xs" variant="light" color="orange">{t('conversions.metaPixel.badgeNoEvents')}</Badge>}
                  </Group>
                  <Text size="xs" c="dimmed" ff="monospace">{px.id}</Text>
                </div>
                <Group gap="xs">
                  <Button size="compact-xs" variant="light" color="brand"
                    leftSection={copiedId === px.id ? <Check size={12} /> : <Copy size={12} />}
                    onClick={() => copySnippet(px.id)}>
                    {copiedId === px.id ? t('conversions.metaPixel.copied') : t('conversions.metaPixel.copyCode')}
                  </Button>
                  <Button size="compact-xs" variant="subtle" color="gray"
                    onClick={() => setShowSnippet(showSnippet === px.id ? null : px.id)}>
                    {showSnippet === px.id ? t('conversions.metaPixel.hide') : t('conversions.metaPixel.showCode')}
                  </Button>
                </Group>
              </Group>
              {showSnippet === px.id && (
                <pre className="mt-2 bg-gray-50 border border-gray-100 rounded p-2 text-[10px] leading-snug overflow-x-auto">{snippetFor(px.id)}</pre>
              )}
              {(px.events_7d || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {px.events_7d.slice(0, 6).map(ev => (
                    <span key={ev.event} className="text-[10px] bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5">
                      {ev.event}: <b>{ev.count.toLocaleString('pl-PL')}</b> ({t('conversions.metaPixel.sevenDays')})
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div>
            <Group justify="space-between" mt={4}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>{t('conversions.metaPixel.customTitle')}</Text>
              <Button size="compact-xs" variant="light" color="brand" leftSection={<Plus size={12} />} onClick={() => setShowConvForm(!showConvForm)}>
                {t('conversions.metaPixel.addConversion')}
              </Button>
            </Group>
            {conversions.length > 0 ? (
              <Stack gap={4} mt="xs">
                {conversions.map(cv => (
                  <Group key={cv.id} gap="xs">
                    <Text size="xs" fw={500}>{cv.name}</Text>
                    <Text size="xs" c="dimmed">{cv.custom_event_type}</Text>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="xs" c="dimmed" mt={4}>{t('conversions.metaPixel.emptyConversions')}</Text>
            )}
            {showConvForm && (
              <Stack gap="xs" mt="sm" p="sm" className="bg-gray-50 rounded-md">
                <TextInput size="xs" label={t('conversions.metaPixel.form.name')} value={convForm.name}
                  onChange={e => setConvForm({ ...convForm, name: e.target.value })} placeholder={t('conversions.metaPixel.form.namePlaceholder')} />
                <Select size="xs" label={t('conversions.metaPixel.form.pixel')} data={pixels.map(p => ({ value: p.id, label: `${p.name} (${p.id})` }))}
                  value={convForm.pixel_id} onChange={v => setConvForm({ ...convForm, pixel_id: v || '' })} />
                <Select size="xs" label={t('conversions.metaPixel.form.eventOptional')} clearable
                  data={STANDARD_EVENTS.map(ev => ({ value: ev, label: ev }))}
                  value={convForm.event || null} onChange={v => setConvForm({ ...convForm, event: v || '' })} />
                <TextInput size="xs" label={t('conversions.metaPixel.form.urlContainsOptional')} value={convForm.url_contains}
                  onChange={e => setConvForm({ ...convForm, url_contains: e.target.value })} placeholder={t('conversions.metaPixel.form.urlPlaceholder')} />
                <Group gap="xs">
                  <Button size="compact-xs" color="brand" loading={savingConv} onClick={createConversion}>{t('conversions.metaPixel.form.create')}</Button>
                  <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setShowConvForm(false)}>{t('conversions.metaPixel.form.cancel')}</Button>
                </Group>
              </Stack>
            )}
          </div>
        </Stack>
      )}
    </Paper>
  );
}
